import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Chip,
  IconButton,
  Button,
  Divider,
} from '@mui/material';

import { get, put } from '../../services/https';
import { useNavigate, useParams } from 'react-router-dom';

/* ================= ICONS ================= */

import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import SellOutlinedIcon from '@mui/icons-material/SellOutlined';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';

import AcUnitIcon from '@mui/icons-material/AcUnit';
import FlashOnOutlinedIcon from '@mui/icons-material/FlashOnOutlined';
import SettingsInputComponentOutlinedIcon from '@mui/icons-material/SettingsInputComponentOutlined';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import ElectricBoltOutlinedIcon from '@mui/icons-material/ElectricBoltOutlined';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import ImageNotSupportedOutlinedIcon from '@mui/icons-material/ImageNotSupportedOutlined';
import { EditIcon } from './EditIcon';
import { useAdminPermission } from '../../hooks/useAdminPermission';

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  CircularProgress,
} from '@mui/material';

import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

const ProductDetails: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canEdit } = useAdminPermission();

  const [product, setProduct] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const [selectedImage, setSelectedImage] = useState(0);
  const [openEdit, setOpenEdit] = useState(false);
  const [editing, setEditing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [images, setImages] = useState<string[]>([]);
  const [editForm, setEditForm] = useState<any>({
    name: '',
    description: '',
    images: [],
    category: 'AC',
    subCategory: 'SPLIT_AC',
    brand: '',
    model: '',
    offerLabel: '',
    pricing: {
      mrp: '',
      discountedPrice: '',
      contractorPointPrice: '',
      discountedPercentage: '',
    },
    stock: '',
    minOrderQty: 1,
    specifications: {
      acType: '',
      tonnage: '',
      inverter: false,
      powerConsumption: '',
      powerRating: 0,
      refrigerant: '',
      voltage: '',
      warranty: {
        product: '',
        compressor: '',
      },
    },
    featured: false,
    active: true,
    sku: '',
  });

  const fetchProductDetails = async () => {
    try {
      const res = await get(`/api/v1/admin/shop/product/${id}`);

      const productData = res.data?.data;
      setProduct(productData);

      // Set images and edit form with current data
      if (productData) {
        const productImages = productData.images || [];
        setImages(productImages);

        setEditForm({
          name: productData.name || '',
          description: productData.description || '',
          images: productImages,
          category: productData.category || 'AC',
          subCategory: productData.subCategory || 'SPLIT_AC',
          brand: productData.brand || '',
          model: productData.model || '',
          offerLabel: productData.offerLabel || '',
          pricing: {
            mrp: productData.pricing?.mrp || '',
            discountedPrice: productData.pricing?.discountedPrice || '',
            contractorPointPrice:
              productData.pricing?.contractorPointPrice || '',
            discountedPercentage:
              productData.pricing?.discountedPercentage || '',
          },
          stock: productData.stock || '',
          minOrderQty: productData.minOrderQty || 1,
          specifications: {
            acType: productData.specifications?.acType || '',
            tonnage: productData.specifications?.tonnage || '',
            inverter: productData.specifications?.inverter || false,
            powerConsumption:
              productData.specifications?.powerConsumption || '',
            powerRating: productData.specifications?.powerRating || 0,
            refrigerant: productData.specifications?.refrigerant || '',
            voltage: productData.specifications?.voltage || '',
            warranty: {
              product: productData.specifications?.warranty?.product || '',
              compressor:
                productData.specifications?.warranty?.compressor || '',
            },
          },
          featured: productData.featured || false,
          active: productData.active !== undefined ? productData.active : true,
          sku: productData.sku || '',
        });
      }
    } catch (error) {
      console.error('Failed to fetch product details', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProductDetails();
  }, [id]);

  const handleEditClick = () => {
    navigate(`/products/edit/${id}`);
  };

  const handleCloseEdit = () => {
    setOpenEdit(false);
  };

  const handleEditFormChange = (
    field: string,
    value: any,
    nestedField?: string,
  ) => {
    if (nestedField) {
      setEditForm((prev: any) => ({
        ...prev,
        [field]: {
          ...prev[field],
          [nestedField]: value,
        },
      }));
    } else if (field.includes('.')) {
      // Handle nested fields like specifications.warranty.product
      const [parent, child, grandChild] = field.split('.');
      if (grandChild) {
        setEditForm((prev: any) => ({
          ...prev,
          [parent]: {
            ...prev[parent],
            [child]: {
              ...prev[parent][child],
              [grandChild]: value,
            },
          },
        }));
      } else {
        setEditForm((prev: any) => ({
          ...prev,
          [parent]: {
            ...prev[parent],
            [child]: value,
          },
        }));
      }
    } else {
      setEditForm((prev: any) => ({ ...prev, [field]: value }));
    }
  };

  // ================= IMAGE UPLOAD =================
  const uploadImageToS3 = async (file: File) => {
    try {
      setUploading(true);

      const res = await get('/api/v1/admin/shop/product/generate-presigned-url', {
        params: {
          fileName: file.name,
          fileType: file.type,
          type: 9,
        },
      });

      if (!res.data?.status) throw new Error('Presigned URL failed');

      const presignedUrl = res.data.presignedUrl;

      await fetch(presignedUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': file.type,
        },
        body: file,
      });

      const imageUrl = presignedUrl.split('?')[0];

      const newImages = [...images, imageUrl];
      setImages(newImages);
      handleEditFormChange('images', newImages);
    } catch (error) {
      console.error(error);
      alert('Image upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;

    const selectedFiles = Array.from(e.target.files);

    if (images.length + selectedFiles.length > 5) {
      alert('You can upload maximum 5 images');
      return;
    }

    selectedFiles.forEach((file) => uploadImageToS3(file));
  };

  const handleRemoveImage = (index: number) => {
    const newImages = images.filter((_, i) => i !== index);
    setImages(newImages);
    handleEditFormChange('images', newImages);
  };

  const prevImage = () => {
    if (!product?.images?.length) return;

    setSelectedImage((prev) =>
      prev === 0 ? product.images.length - 1 : prev - 1,
    );
  };

  const nextImage = () => {
    if (!product?.images?.length) return;

    setSelectedImage((prev) =>
      prev === product.images.length - 1 ? 0 : prev + 1,
    );
  };

  const handleUpdateProduct = async () => {
    try {
      setEditing(true);

      // Prepare payload
      const payload = {
        name: editForm.name,
        description: editForm.description,
        images: editForm.images,
        category: editForm.category,
        subCategory: editForm.subCategory,
        brand: editForm.brand,
        model: editForm.model,
        offerLabel: editForm.offerLabel,
        pricing: {
          mrp: Number(editForm.pricing.mrp),
          discountedPrice: Number(editForm.pricing.discountedPrice) || 0,
          contractorPointPrice:
            Number(editForm.pricing.contractorPointPrice) || 0,
          discountedPercentage:
            Number(editForm.pricing.discountedPercentage) || 0,
        },
        stock: Number(editForm.stock),
        minOrderQty: Number(editForm.minOrderQty),
        specifications: {
          acType: editForm.specifications.acType,
          tonnage: Number(editForm.specifications.tonnage),
          inverter: editForm.specifications.inverter,
          powerConsumption: editForm.specifications.powerConsumption,
          powerRating: editForm.specifications.powerRating,
          refrigerant: editForm.specifications.refrigerant,
          voltage: editForm.specifications.voltage,
          warranty: {
            product: editForm.specifications.warranty.product,
            compressor: editForm.specifications.warranty.compressor,
          },
        },
        featured: editForm.featured,
        active: editForm.active,
        sku: editForm.sku,
      };

      await put(`/api/v1/admin/shop/product/update/${id}`, payload);

      // Refresh product data
      await fetchProductDetails();
      handleCloseEdit();
      alert('Product updated successfully!');
    } catch (error) {
      console.error('Failed to update product', error);
      alert('Failed to update product');
    } finally {
      setEditing(false);
    }
  };

  const specs = product?.specifications || {};
  const specSections = [
    {
      title: 'Key Feature',
      icon: <AcUnitIcon />,
      items: [
        { label: 'AC Type', value: specs.acType },
        { label: 'Tonnage', value: specs.tonnage },
        {
          label: 'Inverter',
          value:
            specs.inverter === undefined
              ? ''
              : specs.inverter
                ? 'Yes'
                : 'No',
        },
        { label: 'Star Rating', value: specs.starRating },
        { label: 'Manufacturing Year', value: specs.manufacturingYear },
        { label: 'Copper Kit', value: specs.copperKit?.toString() },
        { label: 'Country of Origin', value: product?.countryOfOrigin },
      ],
    },
    {
      title: 'Energy Efficiency',
      icon: <ElectricBoltOutlinedIcon />,
      items: [
        { label: 'ISEER', value: specs.iseer },
        { label: 'Power Consumption', value: specs.powerConsumption },
        { label: 'Power Requirement', value: specs.powerRequirement },
        { label: 'Power Supply', value: specs.powerSupply },
        { label: 'Rated Current', value: specs.ratedCurrent },
      ],
    },
    {
      title: 'Cooling Performance',
      icon: <ScienceOutlinedIcon />,
      items: [
        { label: 'Cooling Capacity', value: specs.coolingCapacity },
        { label: 'Compressor Type', value: specs.compressorType },
        { label: 'Refrigerant', value: specs.refrigerant },
        {
          label: 'Ambient Temperature',
          value: specs.ambientTemperature?.toString(),
        },
        { label: 'Convertible Mode', value: specs.convertibleMode?.toString() },
        { label: 'Condenser Coil', value: specs.condenserCoil },
        { label: 'Coverage Area', value: specs.coverageArea },
      ],
    },
    {
      title: 'Air Flow & Filtration',
      icon: <SettingsInputComponentOutlinedIcon />,
      items: [
        { label: 'Air Flow Direction', value: specs.airFlowDirection },
        { label: 'Air Flow Volume', value: specs.airFlowVolume },
        { label: 'Dust Filter', value: specs.dustFilter?.toString() },
        { label: 'Anti-bacteria', value: specs.antiBacteria?.toString() },
      ],
    },
    {
      title: 'Physical Dimensions & Build',
      icon: <Inventory2OutlinedIcon />,
      items: [
        { label: 'Indoor Unit Dimensions', value: specs.indoorUnitDimensions },
        {
          label: 'Outdoor Unit Dimensions',
          value: specs.outdoorUnitDimensions,
        },
        { label: 'Indoor Unit Weight', value: specs.indoorUnitWeight },
        { label: 'Outdoor Unit Weight', value: specs.outdoorUnitWeight },
        { label: 'Body Material', value: specs.bodyMaterial },
        { label: 'Colour', value: specs.color },
      ],
    },
    {
      title: 'Installation & Hardware',
      icon: <TuneOutlinedIcon />,
      items: [
        {
          label: 'Communication Cable Size',
          value: specs.communicationCableSize,
        },
        { label: 'Connecting Pipe Length', value: specs.connectingPipeLength },
        { label: 'Copper Pipe Size', value: specs.copperPipeSize },
      ],
    },
    {
      title: 'Smart Features & Controls',
      icon: <FlashOnOutlinedIcon />,
      items: [
        { label: 'Remote Control', value: specs.remoteControl?.toString() },
        { label: 'Sleep Mode', value: specs.sleepMode?.toString() },
        { label: 'Auto Restart', value: specs.autoRestart?.toString() },
        { label: 'Self Diagnosis', value: specs.selfDiagnosis?.toString() },
        { label: 'Timer', value: specs.timer?.toString() },
        { label: 'Display', value: specs.display?.toString() },
        {
          label: 'Wi‑Fi Connectivity',
          value: specs.wifiConnectivity?.toString(),
        },
      ],
    },
    {
      title: 'Noise & Sound',
      icon: <BoltOutlinedIcon />,
      items: [
        { label: 'Noise Level', value: specs.noiseLevel },
        { label: 'Quiet Mode', value: specs.quietMode?.toString() },
      ],
    },
    {
      title: 'Special Features',
      icon: <VerifiedOutlinedIcon />,
      items: [
        {
          label: 'Special Features',
          value: Array.isArray(specs.specialFeatures)
            ? specs.specialFeatures.join(', ')
            : specs.specialFeatures,
        },
      ],
    },
    {
      title: 'Warranty',
      icon: <WorkspacePremiumOutlinedIcon />,
      items: [
        { label: 'Product Warranty', value: specs.warranty?.product },
        { label: 'Compressor Warranty', value: specs.warranty?.compressor },
        { label: 'PCB Warranty', value: specs.warranty?.pcb },
      ],
    },
  ];

  if (loading) return <Typography sx={{ p: 3 }}>Loading...</Typography>;
  if (!product) return <Typography sx={{ p: 3 }}>No product found</Typography>;

  return (
    <>
      <Box
        sx={{
          background: '#0b0b0b',
          minHeight: '100vh',
          p: {
            xs: 2,
            md: 3,
          },
        }}
      >
        {/* ================================================= */}
        {/* MAIN CONTAINER */}
        {/* ================================================= */}

        <Box
          sx={{
            background: '#141414',
            border: '1px solid #242424',
            borderRadius: '20px',
            overflow: 'hidden',
            boxShadow: '0 0 0 1px rgba(255,255,255,0.03)',
          }}
        >
          {/* ================================================= */}
          {/* STICKY HEADER */}
          {/* ================================================= */}

          <Box
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 20,
              backdropFilter: 'blur(12px)',
              background: 'rgba(20,20,20,0.9)',
              borderBottom: '1px solid #242424',
              px: {
                xs: 2,
                md: 3,
              },
              py: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            {/* LEFT */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 1,
              }}
            >
              <IconButton
                onClick={() => navigate(-1)}
                sx={{ color: '#fff', mr: 0.5 }}
              >
                <ArrowBackIcon />
              </IconButton>
              <Typography
                sx={{
                  color: '#fff',
                  fontSize: 18,
                  fontWeight: 700,
                  marginRight: 1.5,
                }}
              >
                Features Product
              </Typography>

              {canEdit('products') ? (
                <EditIcon size={20} onClick={handleEditClick} />
              ) : null}
            </Box>
            {/* RIGHT ACTIONS */}
          </Box>

          {/* ================================================= */}
          {/* OVERVIEW + GALLERY */}
          {/* ================================================= */}

          <Grid container>
            {/* ================================================= */}
            {/* LEFT SIDE */}
            {/* ================================================= */}

            <Grid
              item
              xs={12}
              lg={7}
              sx={{
                p: {
                  xs: 1.5,
                  md: 1.8,
                },

                borderRight: {
                  lg: '1px solid #1f1f1f',
                },
              }}
            >
              {/* ================================================= */}
              {/* PRODUCT HEADER */}
              {/* ================================================= */}

              <Box
                sx={{
                  mb: 1.2,

                  background:
                    'linear-gradient(180deg, #131313 0%, #0f0f0f 100%)',

                  border: '1px solid #202020',

                  borderRadius: '16px',

                  px: 1.6,
                  py: 1.5,
                }}
              >
                {/* TOP */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',

                    gap: 1.5,
                    flexWrap: 'wrap',
                  }}
                >
                  {/* LEFT */}
                  <Box
                    sx={{
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    {/* LABEL */}
                    <Typography
                      sx={{
                        color: '#6e6e6e',

                        fontSize: 10,
                        fontWeight: 600,

                        letterSpacing: 0.7,
                        textTransform: 'uppercase',

                        mb: 0.7,
                      }}
                    >
                      Product Name
                    </Typography>

                    {/* NAME */}
                    <Typography
                      sx={{
                        color: '#fff',

                        fontSize: {
                          xs: 12,
                          md: 12,
                        },

                        fontWeight: 300,
                        lineHeight: 1.28,

                        mb: 0.9,
                      }}
                    >
                      {product?.name}
                    </Typography>
                  </Box>

                  {/* STATUS */}
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 0.7,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Chip
                      label={product?.active ? 'ACTIVE' : 'INACTIVE'}
                      size="small"
                      sx={{
                        height: 24,

                        background: '#22c55e15',
                        color: '#22c55e',

                        border: '1px solid #22c55e25',

                        fontSize: 10,
                        fontWeight: 700,
                      }}
                    />

                    <Chip
                      label={product?.featured ? 'FEATURED' : 'STANDARD'}
                      size="small"
                      sx={{
                        height: 24,

                        background: '#8b5cf615',
                        color: '#8b5cf6',

                        border: '1px solid #8b5cf625',

                        fontSize: 10,
                        fontWeight: 700,
                      }}
                    />
                  </Box>
                </Box>
              </Box>

              {/* ================================================= */}
              {/* PRODUCT OVERVIEW */}
              {/* ================================================= */}

              <Box
                sx={{
                  mb: 1.2,

                  background: '#111',

                  border: '1px solid #202020',
                  borderRadius: '14px',

                  overflow: 'hidden',
                }}
              >
                {[
                  ['Category', product?.category],
                  ['Sub Category', product?.subCategory],
                  ['Brand', product?.brand],
                  ['SKU', product?.sku],
                  ['Product ID', product?.productId],
                  ['Model Number', product?.model],
                ].map(([label, value], index) => (
                  <Box
                    key={label}
                    sx={{
                      px: 1.5,
                      py: 1.2,

                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',

                      gap: 2,

                      borderBottom: index !== 5 ? '1px solid #1d1d1d' : 'none',
                    }}
                  >
                    <Typography
                      sx={{
                        color: '#727272',

                        fontSize: 11,
                        fontWeight: 500,
                      }}
                    >
                      {label}
                    </Typography>

                    <Typography
                      sx={{
                        color: '#fff',

                        fontSize: 12,
                        fontWeight: 600,

                        textAlign: 'right',

                        wordBreak: 'break-word',
                      }}
                    >
                      {value || '-'}
                    </Typography>
                  </Box>
                ))}
              </Box>
              {/* ================================================= */}
              {/* BOTTOM META */}
              {/* ================================================= */}

              <Box
                sx={{
                  mt: 1,

                  display: 'grid',

                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(3, minmax(0,1fr))',
                  },

                  gap: 1,
                }}
              >
                {[
                  {
                    label: 'Stock',
                    value: product?.stock || 0,
                    icon: <Inventory2OutlinedIcon />,
                    color: '#06b6d4',
                  },

                  {
                    label: 'Min Order',
                    value: product?.minOrderQty || 1,
                    icon: <SellOutlinedIcon />,
                    color: '#f59e0b',
                  },

                  {
                    label: 'Offer Label',
                    value: product?.offerLabel || 'N/A',
                    icon: <VerifiedOutlinedIcon />,
                    color: '#8b5cf6',
                  },
                ].map((item) => (
                  <Box
                    key={item.label}
                    sx={{
                      background: '#111',

                      border: '1px solid #202020',

                      borderRadius: '12px',

                      px: 1,
                      py: 0.95,

                      display: 'flex',
                      alignItems: 'center',

                      gap: 0.9,

                      minHeight: 56,
                    }}
                  >
                    {/* ICON */}
                    <Box
                      sx={{
                        width: 30,
                        height: 30,

                        minWidth: 30,

                        borderRadius: '9px',

                        background: `${item.color}12`,
                        border: `1px solid ${item.color}20`,

                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',

                        color: item.color,

                        '& svg': {
                          fontSize: 15,
                        },
                      }}
                    >
                      {item.icon}
                    </Box>

                    {/* CONTENT */}
                    <Box
                      sx={{
                        minWidth: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          color: '#727272',

                          fontSize: 9,
                          lineHeight: 1,

                          mb: 0.4,
                        }}
                      >
                        {item.label}
                      </Typography>

                      <Typography
                        sx={{
                          color: '#fff',

                          fontSize: 12,
                          fontWeight: 600,

                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {item.value}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Grid>
            {/* ================================================= */}
            {/* RIGHT GALLERY */}
            {/* ================================================= */}

            <Grid
              item
              xs={12}
              lg={5}
              sx={{
                p: {
                  xs: 1.5,
                  md: 2.5,
                },
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
              }}
            >
              {/* MAIN IMAGE */}
              <Box
                sx={{
                  width: '100%',
                  maxWidth: 430,
                  height: 320,
                  background: 'linear-gradient(135deg,#ffffff 0%,#f5f5f5 100%)',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  position: 'relative',
                  border: '1px solid #2a2a2a',
                  transition: 'all 0.25s ease',
                }}
              >
                {product?.images?.length > 0 ? (
                  <Box
                    component="img"
                    src={product?.images?.[selectedImage]}
                    alt="product"
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      transition: 'transform 0.3s ease',
                      '&:hover': {
                        transform: 'scale(1.04)',
                      },
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexDirection: 'column',
                      gap: 2,
                    }}
                  >
                    <ImageNotSupportedOutlinedIcon
                      sx={{
                        fontSize: 50,
                        color: '#999',
                      }}
                    />

                    <Typography
                      sx={{
                        color: '#666',
                      }}
                    >
                      No Product Image Available
                    </Typography>
                  </Box>
                )}

                {/* COUNTER */}
                {product?.images?.length > 0 && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 14,
                      right: 14,
                      background: 'rgba(0,0,0,0.75)',
                      borderRadius: '20px',
                      px: 1.5,
                      py: 0.6,
                    }}
                  >
                    <Typography
                      sx={{
                        color: '#fff',
                        fontSize: 12,
                        fontWeight: 600,
                      }}
                    >
                      {selectedImage + 1}/{product?.images?.length}
                    </Typography>
                  </Box>
                )}

                {/* LEFT BUTTON */}
                {product?.images?.length > 1 && (
                  <IconButton onClick={prevImage} sx={sliderButton('left')}>
                    <ChevronLeftIcon />
                  </IconButton>
                )}

                {/* RIGHT BUTTON */}
                {product?.images?.length > 1 && (
                  <IconButton onClick={nextImage} sx={sliderButton('right')}>
                    <ChevronRightIcon />
                  </IconButton>
                )}
              </Box>

              {/* THUMBNAILS */}
              <Box
                sx={{
                  display: 'flex',
                  gap: 1.5,
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                }}
              >
                {product?.images?.map((image: string, index: number) => (
                  <Box
                    key={index}
                    onClick={() => setSelectedImage(index)}
                    sx={{
                      width: 72,
                      height: 72,
                      borderRadius: '10px',
                      overflow: 'hidden',
                      cursor: 'pointer',
                      background: '#fff',
                      border:
                        selectedImage === index
                          ? '2px solid #ff4d4f'
                          : '1px solid #2b2b2b',
                      transition: 'all 0.25s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                      },
                    }}
                  >
                    <Box
                      component="img"
                      src={image}
                      alt="thumb"
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                      }}
                    />
                  </Box>
                ))}
              </Box>
            </Grid>
          </Grid>

          <Divider />
          {/* ================================================= */}
          {/* DESCRIPTION */}
          {/* ================================================= */}

          <SectionWrapper title="Description">
            <Box
              sx={{
                background: '#171717',
                border: '1px solid #262626',
                borderRadius: '16px',
                p: {
                  xs: 1.5,
                  md: 3,
                },
              }}
            >
              <Typography
                sx={{
                  color: '#9a9a9a',
                  fontSize: 14,
                  lineHeight: 1.9,
                  whiteSpace: 'pre-line',
                }}
              >
                {product?.description || '-'}
              </Typography>
            </Box>
          </SectionWrapper>

          {/* ================================================= */}
          {/* PRICING */}
          {/* ================================================= */}

          <SectionWrapper title="Pricing">
            <Grid container spacing={2.2}>
              {[
                ['MRP', product?.pricing?.mrp, '#4f46e5', ''],
                [
                  'Discount Price',
                  product?.pricing?.discountedPrice,
                  '#16a34a',
                  'SAVE 20%',
                ],
                [
                  'Customer Price',
                  product?.pricing?.customerPrice,
                  '#ff7a00',
                  '',
                ],
                [
                  'Contractor Price',
                  product?.pricing?.contractorPointPrice,
                  '#ff4d4f',
                  '',
                ],
              ].map(([label, value, color, tag]) => (
                <Grid item xs={12} sm={6} md={3} key={label}>
                  <Box
                    sx={{
                      background: '#111',
                      border: '1px solid #202020',
                      borderRadius: '14px',

                      px: 1.6,
                      py: 1.5,

                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.4,

                      minHeight: 82,

                      position: 'relative',

                      transition: 'all 0.2s ease',

                      '&:hover': {
                        borderColor: '#303030',
                        background: '#151515',
                      },
                    }}
                  >
                    {/* ICON */}
                    <Box
                      sx={{
                        width: 42,
                        height: 42,
                        minWidth: 42,

                        borderRadius: '12px',

                        background: `${color}15`,
                        border: `1px solid ${color}25`,

                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',

                        color,
                        fontSize: 18,
                        fontWeight: 700,
                      }}
                    >
                      ₹
                    </Box>

                    {/* CONTENT */}
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        sx={{
                          color: '#7d7d7d',
                          fontSize: 11,
                          fontWeight: 500,
                          mb: 0.4,
                        }}
                      >
                        {label}
                      </Typography>

                      <Typography
                        sx={{
                          color,
                          fontSize: 22,
                          fontWeight: 700,
                          lineHeight: 1,
                        }}
                      >
                        ₹ {Number(value || 0).toLocaleString()}
                      </Typography>
                    </Box>

                    {/* TAG */}
                    {tag && (
                      <Chip
                        label={tag}
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: 10,
                          right: 10,

                          height: 22,

                          background: '#16a34a15',
                          color: '#22c55e',

                          border: '1px solid #16a34a30',

                          fontSize: 10,
                          fontWeight: 700,
                        }}
                      />
                    )}
                  </Box>
                </Grid>
              ))}
            </Grid>
          </SectionWrapper>

          {/* ================================================= */}
          {/* SPECIFICATIONS */}
          {/* ================================================= */}

          {specSections.map((section) => (
            <SectionWrapper title={section.title} key={section.title}>
              <Grid container spacing={2.2}>
                {section.items.map((item) => (
                  <Grid item xs={12} sm={6} lg={3} key={item.label}>
                    <SpecCard
                      icon={section.icon}
                      label={item.label}
                      value={item.value}
                    />
                  </Grid>
                ))}
              </Grid>
            </SectionWrapper>
          ))}

          {/* ================================================= */}
          {/* INVENTORY */}
          {/* ================================================= */}

          <SectionWrapper title="Inventory & Status">
            <Grid container spacing={2.2}>
              {[
                ['Stock Available', product?.stock],
                ['Minimum Order Qty', product?.minOrderQty],
              ].map(([label, value]) => (
                <Grid item xs={12} sm={6} lg={3} key={label}>
                  <InfoCard label={label} value={value} />
                </Grid>
              ))}
            </Grid>
          </SectionWrapper>
        </Box>
      </Box>
      {openEdit && (
        <EditProductDialog
          open={openEdit}
          onClose={handleCloseEdit}
          editForm={editForm}
          handleEditFormChange={handleEditFormChange}
          handleUpdateProduct={handleUpdateProduct}
          editing={editing}
          uploading={uploading}
          images={images}
          handleImageSelect={handleImageSelect}
          handleRemoveImage={handleRemoveImage}
        />
      )}
      ;
    </>
  );
};

/* ================================================= */
/* SECTION WRAPPER */
/* ================================================= */

const SectionWrapper = ({ title, children }: any) => {
  return (
    <Box
      sx={{
        mt: 3,
        px: {
          xs: 1.5,
          md: 2.5,
        },
        pb: 1,
      }}
    >
      <Typography
        sx={{
          color: '#fff',
          fontSize: 17,
          fontWeight: 700,
          mb: 3,
        }}
      >
        {title}
      </Typography>

      {children}
    </Box>
  );
};

interface Props {
  open: boolean;
  onClose: () => void;
  editForm: any;
  handleEditFormChange: any;
  handleUpdateProduct: () => void;
  editing: boolean;
  uploading: boolean;
  images: string[];
  handleImageSelect: any;
  handleRemoveImage: (index: number) => void;
}

const CATEGORY_ENUM = [
  'AC',
  'TOOLS',
  'SPARE_PARTS',
  'CONSUMABLES',
  'ACCESSORIES',
];

const AC_TYPE_ENUM = [
  'SPLIT',
  'WINDOW',
  'CASSETTE',
  'DUCTABLE',
  'TOWER',
  'VRF',
  'VRV',
  'CHILLER',
  'CONCEALED',
];

const EditProductDialog: React.FC<Props> = ({
  open,
  onClose,
  editForm,
  handleEditFormChange,
  handleUpdateProduct,
  editing,
  uploading,
  images,
  handleImageSelect,
  handleRemoveImage,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: '#1e1e1e',
          color: '#ffffff',
          borderRadius: '16px',
          maxHeight: '92vh',
        },
      }}
    >
      {/* HEADER */}
      <DialogTitle
        sx={{
          p: 2.5,
          borderBottom: '1px solid #2a2a2a',
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography
            sx={{
              fontSize: 20,
              fontWeight: 700,
              color: '#fff',
            }}
          >
            Edit Product
          </Typography>

          <IconButton
            onClick={onClose}
            sx={{
              color: '#aaa',
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      {/* CONTENT */}
      <DialogContent
        sx={{
          p: 3,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
          }}
        >
          {/* BASIC */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography sx={labelStyle}>Product Name</Typography>

              <TextField
                fullWidth
                value={editForm.name}
                onChange={(e) => handleEditFormChange('name', e.target.value)}
                sx={textFieldStyle}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography sx={labelStyle}>Brand</Typography>

              <TextField
                fullWidth
                value={editForm.brand}
                onChange={(e) => handleEditFormChange('brand', e.target.value)}
                sx={textFieldStyle}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography sx={labelStyle}>Model</Typography>

              <TextField
                fullWidth
                value={editForm.model}
                onChange={(e) => handleEditFormChange('model', e.target.value)}
                sx={textFieldStyle}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography sx={labelStyle}>Category</Typography>

              <FormControl fullWidth>
                <Select
                  value={editForm.category}
                  onChange={(e) =>
                    handleEditFormChange('category', e.target.value)
                  }
                  sx={selectStyle}
                >
                  {CATEGORY_ENUM.map((c) => (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* DESCRIPTION */}
          <Box>
            <Typography sx={labelStyle}>Description</Typography>

            <TextField
              fullWidth
              multiline
              rows={4}
              value={editForm.description}
              onChange={(e) =>
                handleEditFormChange('description', e.target.value)
              }
              sx={textFieldStyle}
            />
          </Box>

          {/* IMAGES */}
          <Box>
            <Typography sx={labelStyle}>Product Images (Max 5)</Typography>

            <Box
              sx={{
                border: '1px dashed #333',
                borderRadius: '14px',
                p: 3,
                textAlign: 'center',
                mt: 1,
              }}
            >
              <CloudUploadIcon
                sx={{
                  fontSize: 40,
                  color: '#666',
                  mb: 1,
                }}
              />

              <Typography
                sx={{
                  color: '#aaa',
                  mb: 2,
                }}
              >
                Upload Product Images
              </Typography>

              <Button
                component="label"
                variant="outlined"
                disabled={uploading || images.length >= 5}
                sx={{
                  color: '#fff',
                  borderColor: '#444',
                }}
              >
                {uploading ? 'Uploading...' : 'Select Images'}

                <input
                  hidden
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageSelect}
                />
              </Button>

              {/* PREVIEW */}
              <Box
                sx={{
                  display: 'flex',
                  gap: 1.5,
                  flexWrap: 'wrap',
                  mt: 3,
                  justifyContent: 'center',
                }}
              >
                {images.map((image, index) => (
                  <Box
                    key={index}
                    sx={{
                      position: 'relative',
                    }}
                  >
                    <Box
                      component="img"
                      src={image}
                      alt="product"
                      sx={{
                        width: 90,
                        height: 90,
                        borderRadius: '12px',
                        objectFit: 'cover',
                        border: '1px solid #333',
                      }}
                    />

                    <Button
                      size="small"
                      color="error"
                      onClick={() => handleRemoveImage(index)}
                      sx={{
                        minWidth: 28,
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        position: 'absolute',
                        top: -8,
                        right: -8,
                        background: '#000',
                      }}
                    >
                      ×
                    </Button>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>

          {/* SPECIFICATIONS */}
          <Typography
            sx={{
              color: '#fff',
              fontSize: 18,
              fontWeight: 700,
            }}
          >
            Specifications
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography sx={labelStyle}>AC Type</Typography>

              <FormControl fullWidth>
                <Select
                  value={editForm.specifications.acType}
                  onChange={(e) =>
                    handleEditFormChange(
                      'specifications',
                      e.target.value,
                      'acType',
                    )
                  }
                  sx={selectStyle}
                >
                  {AC_TYPE_ENUM.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography sx={labelStyle}>Tonnage</Typography>

              <TextField
                fullWidth
                value={editForm.specifications.tonnage}
                onChange={(e) =>
                  handleEditFormChange(
                    'specifications',
                    e.target.value,
                    'tonnage',
                  )
                }
                sx={textFieldStyle}
              />
            </Grid>
          </Grid>

          {/* TOGGLES */}
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={editForm.featured}
                  onChange={(e) =>
                    handleEditFormChange('featured', e.target.checked)
                  }
                />
              }
              label="Featured"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={editForm.active}
                  onChange={(e) =>
                    handleEditFormChange('active', e.target.checked)
                  }
                />
              }
              label="Active"
            />
          </Box>
        </Box>
      </DialogContent>

      {/* FOOTER */}
      <DialogActions
        sx={{
          p: 2.5,
          borderTop: '1px solid #2a2a2a',
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderColor: '#444',
            color: '#fff',
          }}
        >
          Cancel
        </Button>

        <Button
          onClick={handleUpdateProduct}
          variant="contained"
          disabled={editing}
          sx={{
            background: '#E31E25',
            px: 3,
            fontWeight: 700,
          }}
        >
          {editing ? (
            <CircularProgress size={22} color="inherit" />
          ) : (
            'Update Product'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* ================= STYLES ================= */

const labelStyle = {
  color: '#9ca3af',
  mb: 1,
  fontSize: 13,
  fontWeight: 500,
};

const textFieldStyle = {
  '& .MuiOutlinedInput-root': {
    color: '#fff',
    background: '#141414',

    '& fieldset': {
      borderColor: '#2d2d2d',
    },

    '&:hover fieldset': {
      borderColor: '#444',
    },

    '&.Mui-focused fieldset': {
      borderColor: '#E31E25',
    },
  },
};

const selectStyle = {
  color: '#fff',
  background: '#141414',

  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#2d2d2d',
  },

  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#444',
  },

  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#E31E25',
  },

  '& .MuiSvgIcon-root': {
    color: '#aaa',
  },
};

/* ================================================= */
/* INFO CARD */
/* ================================================= */

const InfoCard = ({ label, value }: any) => {
  const isActive = value === 'Active' || value === 'Yes';

  return (
    <Box
      sx={{
        background: '#111',
        border: '1px solid #202020',
        borderRadius: '14px',

        px: 1.6,
        py: 1.5,

        display: 'flex',
        alignItems: 'center',
        gap: 1.4,

        minHeight: 82,

        transition: 'all 0.2s ease',

        '&:hover': {
          borderColor: '#303030',
          background: '#151515',
        },
      }}
    >
      {/* ICON */}
      <Box
        sx={{
          width: 40,
          height: 40,
          minWidth: 40,

          borderRadius: '12px',

          background: isActive
            ? 'rgba(34,197,94,0.12)'
            : 'rgba(255,255,255,0.05)',

          border: isActive
            ? '1px solid rgba(34,197,94,0.2)'
            : '1px solid rgba(255,255,255,0.05)',

          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',

          color: isActive ? '#22c55e' : '#8b8b8b',

          fontSize: 18,
          fontWeight: 700,
        }}
      >
        {typeof value === 'number' ? '#' : '✓'}
      </Box>

      {/* CONTENT */}
      <Box sx={{ flex: 1 }}>
        <Typography
          sx={{
            color: '#7d7d7d',
            fontSize: 11,
            fontWeight: 500,
            mb: 0.4,
          }}
        >
          {label}
        </Typography>

        <Typography
          sx={{
            color: '#fff',
            fontSize: 18,
            fontWeight: 700,
            lineHeight: 1.1,
          }}
        >
          {value || '-'}
        </Typography>
      </Box>
    </Box>
  );
};

/* ================================================= */
/* SPEC CARD */
/* ================================================= */

const SpecCard = ({ icon, label, value }: any) => {
  return (
    <Box
      sx={{
        background: '#131313',
        border: '1px solid #222',
        borderRadius: '14px',

        px: 1.6,
        py: 1.4,

        display: 'flex',
        alignItems: 'center',
        gap: 1.4,

        minHeight: 78,

        transition: 'all 0.2s ease',

        '&:hover': {
          borderColor: '#333',
          background: '#161616',
        },
      }}
    >
      {/* ICON */}
      <Box
        sx={{
          width: 38,
          height: 38,
          minWidth: 38,

          borderRadius: '10px',

          background: '#1d1d1d',

          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',

          color: '#ff5a5f',

          '& svg': {
            fontSize: 18,
          },
        }}
      >
        {icon}
      </Box>

      {/* CONTENT */}
      <Box
        sx={{
          flex: 1,
          overflow: 'hidden',
        }}
      >
        <Typography
          sx={{
            color: '#7d7d7d',
            fontSize: 11,
            fontWeight: 500,
            mb: 0.3,
            lineHeight: 1.2,
          }}
        >
          {label}
        </Typography>

        <Typography
          sx={{
            color: '#fff',
            fontSize: 16,
            fontWeight: 600,
            lineHeight: 1.3,

            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {value || '-'}
        </Typography>
      </Box>
    </Box>
  );
};
/* ================================================= */
/* META CARD */
/* ================================================= */

const MetaCard = ({ icon, label, value }: any) => {
  return (
    <Box
      sx={{
        background: '#171717',
        border: '1px solid #262626',
        borderRadius: '14px',
        px: 2.5,
        py: 2,
        minWidth: 150,
        transition: 'all 0.25s ease',
        '&:hover': {
          borderColor: '#333',
          transform: 'translateY(-2px)',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 1,
          color: '#8b8b8b',
        }}
      >
        {icon}

        <Typography
          sx={{
            fontSize: 12,
          }}
        >
          {label}
        </Typography>
      </Box>

      <Typography
        sx={{
          color: '#fff',
          fontSize: 16,
          fontWeight: 700,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
};

const ModernInfoCard = ({ label, value, icon, color }: any) => {
  return (
    <Box
      sx={{
        background: '#111',
        border: '1px solid #202020',
        borderRadius: '14px',

        px: 1.6,
        py: 1.5,

        minHeight: 88,

        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.4,

        transition: 'all 0.2s ease',

        '&:hover': {
          borderColor: '#303030',
          background: '#151515',
        },
      }}
    >
      {/* ICON */}
      <Box
        sx={{
          width: 40,
          height: 40,
          minWidth: 40,

          borderRadius: '12px',

          background: `${color}15`,
          border: `1px solid ${color}25`,

          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',

          color,

          '& svg': {
            fontSize: 18,
          },
        }}
      >
        {icon}
      </Box>

      {/* CONTENT */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
        }}
      >
        <Typography
          sx={{
            color: '#7d7d7d',
            fontSize: 11,
            fontWeight: 500,
            mb: 0.6,
            lineHeight: 1.2,
          }}
        >
          {label}
        </Typography>

        <Typography
          sx={{
            color: '#fff',
            fontSize: 14,
            fontWeight: 600,
            lineHeight: 1.5,

            wordBreak: 'break-word',
          }}
        >
          {value || '-'}
        </Typography>
      </Box>
    </Box>
  );
};
/* ================================================= */
/* STYLES */
/* ================================================= */

const valueStyle = {
  color: '#fff',
  fontSize: 15,
  fontWeight: 600,
  lineHeight: 1.6,
};

const statusChip = (color: string) => ({
  background: `${color}20`,
  color,
  border: `1px solid ${color}40`,
  fontWeight: 700,
  fontSize: 11,
});

const sliderButton = (side: 'left' | 'right') => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  [side]: 12,
  background: 'rgba(0,0,0,0.65)',
  color: '#fff',
  '&:hover': {
    background: 'rgba(0,0,0,0.85)',
  },
});
const pricingCardStyle = {
  position: 'relative',
  overflow: 'hidden',
  borderRadius: '16px',
  background: 'linear-gradient(180deg,#141414 0%, #101010 100%)',
  border: '1px solid rgba(255,255,255,0.05)',
  p: 2.5,
  minHeight: 70,

  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',

  transition: 'all 0.28s ease',

  '&:hover': {
    transform: 'translateY(-5px)',
    borderColor: '#303030',
    boxShadow: '0 12px 40px rgba(0,0,0,0.45)',
  },
};

export default ProductDetails;
